import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import {
    createColumnHelper,
    getCoreRowModel,
    getExpandedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { Box } from '@givebutter/primitives/box';
import { useToast } from '@givebutter/primitives/toast';
import { faBellSlash, faCircleQuestion } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchContacts } from 'api/contacts/contacts';
import { fetchEngageMessageById, updateEngageMessageById } from 'api/engage';
import { StepNavigation } from 'assets/js/pages/engage/components/MessageBuilder/StepNavigation';
import { FilterBarContainer } from 'components/filter/filters-next/FilterBarContainer';
import { FilterGroupBar } from 'components/filter/filters-next/FilterGroupBar';
import { FilterProvider } from 'components/filter/filters-next/FilterProvider';
import { SaveNewSegmentButton } from 'components/filter/filters-next/SaveNewSegmentButton';
import { SegmentsIndexDrawer } from 'components/filter/filters-next/SegmentIndexDrawer';
import { transformSavedFilterGroups } from 'components/filter/filters-next/downstream';
import { useFiltersByKey } from 'components/filter/filters-next/useFiltersByKey';
import { useInitFilters } from 'components/filter/filters-next/useInitFilters';
import * as Table from 'components/table';
import { useSyncPaginatedData } from 'components/table/useSyncPaginatedData';
import * as Tooltip from 'components/tooltip';
import { EmptyData } from 'components/util';
import { ContactType } from 'constants/contacts';
import { getContactName } from 'customHooks/contactNameDisplay';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useIsPending } from 'customHooks/useIsPending';
import { getFormattedPhoneNumber } from 'helpers/formatting';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { isEmpty } from 'lodash';
import { accountQueryKeys } from 'queries/account-query-keys';
import { Contact } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

enum ColumnIds {
    FirstName = 'first_name',
    EmailData = 'primary_email',
    EmailAlerts = 'EmailAlerts',
    PhoneData = 'PhoneData',
    PhoneAlerts = 'PhoneAlerts',
}

const columnHelper = createColumnHelper<Contact>();

const StepOne = () => {
    const { error } = useToast();
    const queryClient = useQueryClient();
    const { accountId } = useCurrentAccount();
    const { contactsWithoutName = false } = useFlags();
    const { message_id } = useParams<{ message_id: string }>();
    const messageId = parseInt(message_id);
    const { data: message } = useSuspenseQuery({
        queryKey: accountQueryKeys.engageMessageById(accountId, messageId),
        queryFn: () => fetchEngageMessageById(accountId, messageId),
    });
    const isEmail = message.channel === 'email';
    const isSms = message.channel === 'sms';
    const { didInit, filters } = useFiltersByKey('engage');

    const { pagination, setPagination, sorting, setSorting } = Table.useReactTableState({
        sortingState: [{ id: 'first_name', desc: false }],
    });

    const [isPending, params] = useIsPending(
        {
            type: ContactType.Individual,
            page: pagination.pageIndex + 1,
            hasPrimaryEmail: isEmail && contactsWithoutName ? 'with' : undefined,
            isEmailSubscribed: isEmail && contactsWithoutName ? true : undefined,
            hasPrimaryPhone: isSms && contactsWithoutName ? 'with' : undefined,
            isPhoneSubscribed: isSms && contactsWithoutName ? true : undefined,
            filters,
            ...(sorting.length > 0
                ? { [`sortBy${sorting[0].desc ? 'Desc' : ''}`]: sorting[0].id }
                : {}),
        },
        500,
    );

    const { mutateAsync: updateRecipientFilters } = useMutation({
        mutationFn: () =>
            updateEngageMessageById(accountId, message.id, {
                channel: isEmail ? 'email' : 'sms',
                recipient_filters: filters,
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.engageMessageById(accountId, messageId),
            });
        },
        onError: (e: GivebutterAxiosError) => {
            error(e.response?.data.message ?? 'Unable to sync your filters');
        },
    });

    const { data } = useSuspenseQuery({
        queryKey: accountQueryKeys.contactsWithFilters(accountId, params),
        queryFn: () => fetchContacts(accountId, params),
    });

    const columns = [
        columnHelper.accessor('first_name', {
            header: 'Name',
            cell: info => {
                const contact = info.row.original;

                return getContactName(contact);
            },
            meta: {
                className: 'w-[350px]',
            },
        }),
        columnHelper.accessor(ColumnIds.EmailData, {
            id: ColumnIds.EmailData,
            header: 'Email',
            enableSorting: false,
            cell: () => <Table.CellDataOrEmpty />,
        }),
        columnHelper.display({
            id: ColumnIds.EmailAlerts,
            cell: info => {
                const { is_email_subscribed, primary_email } = info.row.original;
                const hasEmail = !!primary_email;

                return (
                    <div className={'flex items-center gap-2'}>
                        {!hasEmail && (
                            <Tooltip.Root>
                                <Tooltip.Trigger>
                                    <FontAwesomeIcon
                                        className={'text-orange-500'}
                                        icon={faCircleQuestion}
                                    />
                                </Tooltip.Trigger>
                                <Tooltip.Portal>
                                    <Tooltip.Content inverse>
                                        <p>No email address</p>
                                        <Tooltip.Arrow inverse />
                                    </Tooltip.Content>
                                </Tooltip.Portal>
                            </Tooltip.Root>
                        )}
                        {hasEmail && !is_email_subscribed && (
                            <Tooltip.Root>
                                <Tooltip.Trigger>
                                    <FontAwesomeIcon
                                        className={'text-red-500'}
                                        icon={faBellSlash}
                                    />
                                </Tooltip.Trigger>
                                <Tooltip.Portal>
                                    <Tooltip.Content inverse>
                                        <p>Not subscribed</p>
                                        <Tooltip.Arrow inverse />
                                    </Tooltip.Content>
                                </Tooltip.Portal>
                            </Tooltip.Root>
                        )}
                    </div>
                );
            },
            meta: {
                className: '[&>*]:float-right',
            },
        }),
        columnHelper.accessor('primary_phone', {
            id: ColumnIds.PhoneData,
            header: 'Number',
            enableSorting: false,
            cell: info => {
                const phone = info.getValue();

                return phone ? getFormattedPhoneNumber(phone) : <EmptyData />;
            },
        }),
        columnHelper.display({
            id: ColumnIds.PhoneAlerts,
            cell: info => {
                const { is_phone_subscribed, primary_phone: phone } = info.row.original;

                return (
                    <div className={'flex items-center gap-2'}>
                        {!phone && (
                            <Tooltip.Root>
                                <Tooltip.Trigger>
                                    <FontAwesomeIcon
                                        className={'text-orange-500'}
                                        icon={faCircleQuestion}
                                    />
                                </Tooltip.Trigger>
                                <Tooltip.Portal>
                                    <Tooltip.Content inverse>
                                        <p>No phone number</p>
                                        <Tooltip.Arrow inverse />
                                    </Tooltip.Content>
                                </Tooltip.Portal>
                            </Tooltip.Root>
                        )}
                        {phone && !is_phone_subscribed && (
                            <Tooltip.Root>
                                <Tooltip.Trigger>
                                    <FontAwesomeIcon
                                        className={'text-red-500'}
                                        icon={faBellSlash}
                                    />
                                </Tooltip.Trigger>
                                <Tooltip.Portal>
                                    <Tooltip.Content inverse>
                                        <p>Not subscribed</p>
                                        <Tooltip.Arrow inverse />
                                    </Tooltip.Content>
                                </Tooltip.Portal>
                            </Tooltip.Root>
                        )}
                    </div>
                );
            },
            meta: {
                className: '[&>*]:float-right',
            },
        }),
    ];

    const table = useReactTable({
        data: data?.data ?? [],
        columns,
        pageCount: data?.meta.last_page ?? -1,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        state: {
            pagination,
            sorting,
            columnVisibility: {
                [ColumnIds.EmailData]: isEmail,
                [ColumnIds.EmailAlerts]: isEmail,
                [ColumnIds.PhoneData]: !isEmail,
                [ColumnIds.PhoneAlerts]: !isEmail,
            },
            columnOrder: contactsWithoutName
                ? [
                      ColumnIds.EmailData,
                      ColumnIds.PhoneData,
                      ColumnIds.FirstName,
                      ColumnIds.EmailAlerts,
                      ColumnIds.PhoneAlerts,
                  ]
                : [
                      ColumnIds.FirstName,
                      ColumnIds.EmailData,
                      ColumnIds.PhoneData,
                      ColumnIds.EmailAlerts,
                      ColumnIds.PhoneAlerts,
                  ],
        },
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        manualPagination: true,
        manualSorting: true,
        enableHiding: false,
        enableColumnPinning: false,
        meta: {
            to: cell => `/accounts/${accountId}/contacts/${cell.row.original.id}`,
        },
    });

    useSyncPaginatedData(table, data);

    useEffect(() => {
        if (didInit && data) {
            updateRecipientFilters();
        }
    }, [didInit, data, updateRecipientFilters]);

    return (
        <FilterProvider cacheKey={'engage'} resource={'contacts'}>
            <div className={'mt-8 flex flex-col gap-4'}>
                <Table.Root
                    isLoading={isPending}
                    variant={'bordered'}
                    isFiltered={!isEmpty(filters)}
                    table={table}
                    isPersisted={false}
                >
                    <Box className={'flex flex-col'}>
                        <div className={'flex flex-col'}>
                            <div
                                className={'flex justify-end border-b border-b-gray-100 px-4 py-3'}
                            >
                                <SaveNewSegmentButton />
                                <SegmentsIndexDrawer />
                                <Table.ToggleFullWidth />
                            </div>
                            <FilterBarContainer>
                                <FilterGroupBar
                                    quickFilters={[
                                        'total_contributions',
                                        'recurring_contributions',
                                        'created_at',
                                        'tags',
                                    ]}
                                />
                            </FilterBarContainer>
                        </div>
                        <Table.Content>
                            <Table.Header />
                            <Table.Body />
                        </Table.Content>
                        <Table.Empty variant={'primary'}>
                            <Table.EmptyIcon />
                            <Table.EmptyTitle>No contacts found</Table.EmptyTitle>
                        </Table.Empty>
                        <Table.Pagination className={'border-t border-t-gray-100 p-2'}>
                            <Table.PaginationPageNavigation />
                        </Table.Pagination>
                    </Box>
                </Table.Root>
                <StepNavigation />
            </div>
        </FilterProvider>
    );
};

const Wrapper = () => {
    const { accountId } = useCurrentAccount();
    const { message_id } = useParams<{ message_id: string }>();
    const messageId = parseInt(message_id);
    const { data: message } = useSuspenseQuery({
        queryKey: accountQueryKeys.engageMessageById(accountId, messageId),
        queryFn: () => fetchEngageMessageById(accountId, messageId),
    });
    const { didInit } = useFiltersByKey('engage');
    useInitFilters('engage', transformSavedFilterGroups(message.recipient_filters ?? []));

    if (didInit) {
        return <StepOne />;
    }

    return null;
};

export { Wrapper as StepOne };
